import { z } from "zod";
import type { Operation } from "../types";

/**
 * The live-activity feed — "what is the app doing right now". Powers the
 * low-profile bottom-right dock (`LiveActivityDock`): in-flight + just-finished
 * background work unified across subsystems (agent ship/verify runs, optimizer
 * evals, integration syncs, section audits).
 *
 * This is a DIFFERENT feed from the `activity` topic (the rolled-up activity
 * LOG of things that already happened). This one is momentary work with a
 * lifetime: a `running` item with a started clock, or one that finished in the
 * last breath. It rides its own live-bus topic (`work`) so a run starting or
 * finishing — including writes from the out-of-process MCP server — refreshes
 * the dock on the next `<LiveBus>` poll.
 *
 * Timestamps cross the wire as epoch milliseconds (NOT SQLite strings) so the
 * dock can run a pure client-side stopwatch without re-parsing dates.
 */

export const LIVE_ACTIVITY_KINDS = [
  "agent",
  "optimizer",
  "sync",
  "audit",
] as const;
export type LiveActivityKind = (typeof LIVE_ACTIVITY_KINDS)[number];

export const LIVE_ACTIVITY_STATUSES = ["running", "done", "failed"] as const;
export type LiveActivityStatus = (typeof LIVE_ACTIVITY_STATUSES)[number];

const liveActivityItem = z.object({
  /** Stable cross-source key, e.g. `worker:123` / `sync:45` / `audit:7`. */
  id: z.string(),
  kind: z.enum(LIVE_ACTIVITY_KINDS),
  status: z.enum(LIVE_ACTIVITY_STATUSES),
  /** Short headline, e.g. "Shipper" / "Optimizing triage loop" / "Syncing hubspot". */
  title: z.string(),
  /** Sub-line: the current step, the agent id, or the outcome. */
  detail: z.string(),
  /** Epoch ms. */
  startedMs: z.number(),
  /** Epoch ms, or null while still running. */
  endedMs: z.number().nullable(),
  /** Where clicking the row drills in. */
  href: z.string(),
});
export type LiveActivityItem = z.infer<typeof liveActivityItem>;

const listResponse = z.object({ items: z.array(liveActivityItem) });
export type LiveActivityList = z.infer<typeof listResponse>;

const listOp: Operation<void, void, void, LiveActivityList> = {
  method: "GET",
  path: () => "/api/live-activity",
  response: listResponse,
};

export const liveActivity = {
  list: listOp,
};
